import React, { useState } from "react";
import {
  Sparkles, Loader2, Film, Scissors, Copy, Link2, Users, Mic, Gauge, Layers
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePoll } from "@/hooks/usePoll";
import NoProject from "@/components/NoProject";

function Stat({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="studio-card p-4" data-testid={`intel-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={15} style={{ color: accent }} /><span className="metric-label">{label}</span>
      </div>
      <div className="font-head text-2xl font-extrabold">{value}</div>
      {sub && <div className="metric-label" style={{ fontSize: 9 }}>{sub}</div>}
    </div>
  );
}

export default function AIAnalysis() {
  const { projectId } = useApp();
  const [busy, setBusy] = useState("");
  const { data: sum } = usePoll(() => (projectId ? api.analysisSummary(projectId) : Promise.resolve(null)), 3000, [projectId]);
  const { data: jobs } = usePoll(() => (projectId ? api.jobsSummary(projectId) : Promise.resolve(null)), 2500, [projectId]);
  const { data: ev } = usePoll(() => (projectId ? api.events(projectId) : Promise.resolve(null)), 4000, [projectId]);
  const { data: providers } = usePoll(() => api.aiProviders(), 60000, []);

  if (!projectId) return <NoProject title="AI Analysis needs a project" />;

  const run = async (key, fn, msg) => {
    setBusy(key);
    try { const r = await fn(); toast.success(msg + (r?.queued ? ` — ${r.queued} job(s)` : "")); }
    catch (e) { toast.error("Failed to start"); }
    finally { setBusy(""); }
  };

  const active = (jobs?.queued || 0) + (jobs?.running || 0);
  const q = sum?.quality, sc = sum?.scenes, au = sum?.audio, sy = sum?.sync;

  const Action = ({ k, label, icon: Icon, fn, desc }) => (
    <button data-testid={`run-${k}`} onClick={() => run(k, fn, label)} disabled={!!busy}
      className="studio-card p-4 text-left hover:border-[#3B4259] transition-all disabled:opacity-50">
      <div className="flex items-center gap-2 mb-1">
        {busy === k ? <Loader2 size={16} className="animate-spin text-[#E6B04E]" /> : <Icon size={16} className="text-[#E6B04E]" />}
        <span className="font-semibold text-sm">{label}</span>
      </div>
      <p className="text-[#64748B] text-xs">{desc}</p>
    </button>
  );

  return (
    <div className="p-8 fade-up space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#212534] flex items-center justify-center"><Sparkles size={22} className="text-[#E6B04E]" /></div>
          <div>
            <h1 className="font-head text-3xl font-extrabold tracking-tight">AI Analysis</h1>
            <p className="text-[#94A3B8] text-sm">Media intelligence, wedding AI, sync &amp; grouping — real engines via the orchestrator</p>
          </div>
        </div>
        {active > 0 && <span className="flex items-center gap-2 text-[12px] font-mono text-[#00F0FF]"><span className="w-2 h-2 rounded-full bg-[#00F0FF] pulse-dot" /> {active} jobs active</span>}
      </div>

      {/* actions */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Action k="analyze" label="Analyze Media" icon={Gauge} fn={() => api.analyzeProject(projectId)} desc="Scenes, quality scoring, fingerprints, audio profile" />
        <Action k="wedding" label="Wedding AI" icon={Sparkles} fn={() => api.analyzeWedding(projectId)} desc="Transcribe speech, detect faces, tag events & emotion" />
        <Action k="sync" label="Sync Cameras" icon={Link2} fn={() => api.syncProject(projectId)} desc="Timecode + audio-correlation multi-cam sync" />
        <Action k="group" label="Group Similar" icon={Copy} fn={() => api.groupSimilar(projectId)} desc="Duplicate & near-duplicate shot grouping" />
        <Action k="cluster" label="Cluster People" icon={Users} fn={() => api.clusterFaces(projectId)} desc="Group detected faces into person clusters" />
      </div>

      {/* summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat icon={Gauge} label="Avg Quality" value={sum?.avg_score ?? "—"} sub={q ? `${q.done}/${q.total} scored` : ""} accent="#E6B04E" />
        <Stat icon={Scissors} label="Scenes" value={sc?.total_scenes ?? 0} sub={sc ? `${sc.done}/${sc.total} clips` : ""} accent="#00F0FF" />
        <Stat icon={Mic} label="Audio" value={au ? `${au.done}/${au.total}` : "—"} sub={au ? Object.entries(au.classes || {}).map(([k, v]) => `${k}:${v}`).join(" ") : ""} accent="#8B5CF6" />
        <Stat icon={Copy} label="Dup Groups" value={sum?.similarity_groups ?? 0} sub="similar-shot clusters" accent="#F59E0B" />
        <Stat icon={Link2} label="Sync" value={sy ? `${sy.synced}/${sy.total}` : "—"} sub="clips aligned" accent="#10B981" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* detected events */}
        <div className="studio-card p-5 lg:col-span-2">
          <h3 className="font-head font-bold mb-3 flex items-center gap-2"><Film size={16} className="text-[#E6B04E]" /> Detected Wedding Events</h3>
          {ev?.events?.length ? (
            <div className="flex flex-wrap gap-2">
              {ev.events.map((e) => (
                <span key={e.event} className="chip chip-GOOD" data-testid={`event-chip-${e.event}`}>{e.event}<span className="ml-1.5 text-[#64748B]">{e.count}</span></span>
              ))}
            </div>
          ) : <p className="text-[#64748B] text-sm">No events tagged yet. Run <b>Wedding AI</b>.</p>}
        </div>

        {/* providers */}
        <div className="studio-card p-5">
          <h3 className="font-head font-bold mb-3 flex items-center gap-2"><Layers size={16} className="text-[#E6B04E]" /> Orchestrator</h3>
          <div className="space-y-1.5">
            {(providers?.providers || []).map((p) => (
              <div key={p.name} className="flex items-center justify-between text-sm">
                <span className="text-[#94A3B8]">{p.display_name}</span>
                <span className={`w-2 h-2 rounded-full ${p.enabled ? "bg-[#10B981]" : "bg-[#64748B]"}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
